import { useMutation, useQuery, useQueryClient, useSuspenseQuery } from '@tanstack/react-query';
import { getRouteApi, Link } from '@tanstack/react-router';
import { NURSING_NOTE_MAX, VITAL_SIGNS } from '@wodi/domain';
import type { NursingNoteView, NursingView, VitalSignSetView } from '@wodi/shared';
import {
  Button,
  Cell,
  Column,
  Dialog,
  DialogActions,
  Kbd,
  LoadingBlock,
  Notice,
  PageHeader,
  Row,
  Section,
  Table,
  TableBody,
  TableHeader,
  Tag,
  TextArea,
  useShortcut,
} from '@wodi/ui';
import { useRef, useState } from 'react';

import { nursingNoteInError, nursingQuery, recordNursingNote } from '../api/nursing.ts';
import { can, sessionQuery } from '../api/session.ts';
import { useOnline } from '../hooks/use-online.ts';
import { PatientBannerLoader } from '../patients/patient-banner.tsx';
import { REASON_MIN_LENGTH } from '../patients/reason-dialog.tsx';
import { toasts } from '../toasts.ts';
import { errorText } from './bed-board-page.tsx';
import styles from './nursing.module.css';
import { flagWords, VitalsChart, when } from './vitals-chart.tsx';
import { VitalsRoundForm } from './vitals-round-form.tsx';

// Nursing for one stay (docs/plans/phase2-ward-rounds-nursing.md §5): record a vitals round,
// the vitals chart and table over the stay, and nursing notes. The address carries the
// stay's internal ID only.

const route = getRouteApi('/_app/admissions/$admissionId/nursing');

function placeWords(view: NursingView): string {
  const a = view.admission;
  if (a.ward === null) return `Day ${String(a.daysAdmitted)}.`;
  return `${a.ward.name}${a.bedLabel === null ? ', waiting for a bed' : `, bed ${a.bedLabel}`}. Day ${String(a.daysAdmitted)}.`;
}

function setValues(set: VitalSignSetView): string {
  const current = set.observations.filter((o) => o.status === 'final');
  if (current.length === 0) return 'No values';
  return current
    .map((o) => {
      const flag = flagWords(o.interpretation);
      return `${VITAL_SIGNS[o.key].label} ${o.value} ${o.unit.display}${flag === null ? '' : ` (${flag})`}`;
    })
    .join(', ');
}

function VitalsTable({ sets }: { sets: readonly VitalSignSetView[] }) {
  return (
    <Table flat dense aria-label="Vitals in this stay">
      <TableHeader>
        <Column isRowHeader>Measured</Column>
        <Column>Values</Column>
        <Column>Recorded by</Column>
        <Column>State</Column>
      </TableHeader>
      <TableBody items={sets} renderEmptyState={() => 'No vitals recorded in this stay yet.'}>
        {(set) => (
          <Row id={set.id}>
            <Cell>
              <span className={styles.numeric}>{when(set.effectiveAt)}</span>
            </Cell>
            <Cell>{setValues(set)}</Cell>
            <Cell>{set.recordedBy.displayName}</Cell>
            <Cell>
              {set.status === 'entered_in_error' ? (
                <Tag state="neutral">Entered in error</Tag>
              ) : set.observations.some((o) => o.status === 'amended') ? (
                <Tag state="attention">Corrected</Tag>
              ) : (
                <Tag state="done">Recorded</Tag>
              )}
            </Cell>
          </Row>
        )}
      </TableBody>
    </Table>
  );
}

function InErrorDialog({
  note,
  admissionId,
  onClose,
}: {
  note: NursingNoteView;
  admissionId: string;
  onClose: () => void;
}) {
  const queryClient = useQueryClient();
  const [reason, setReason] = useState('');
  const mark = useMutation({
    mutationFn: () => nursingNoteInError(note.id, reason.trim()),
    onSuccess: (next) => {
      queryClient.setQueryData(['inpatient', 'nursing', admissionId], next);
      toasts.show({ title: 'Note entered in error' });
      onClose();
    },
  });
  return (
    <Dialog
      title="Enter note in error"
      isOpen
      onOpenChange={(open) => {
        if (!open) onClose();
      }}
    >
      <p className={styles.meta}>
        The note stays in the history, struck through, with your reason.
      </p>
      <TextArea label="Why is it in error?" value={reason} onChange={setReason} rows={2} />
      {mark.error === null ? null : (
        <Notice tone="error">
          {errorText(mark.error, 'The note was not changed. Try again.')}
        </Notice>
      )}
      <DialogActions>
        <Button variant="quiet" onPress={onClose}>
          Keep note
        </Button>
        <Button
          variant="primary"
          isDisabled={reason.trim().length < REASON_MIN_LENGTH}
          isPending={mark.isPending}
          pendingLabel="Saving…"
          onPress={() => {
            mark.mutate();
          }}
        >
          Enter in error
        </Button>
      </DialogActions>
    </Dialog>
  );
}

function NursingNotes({ view, mayWrite }: { view: NursingView; mayWrite: boolean }) {
  const { data: session } = useSuspenseQuery(sessionQuery);
  const queryClient = useQueryClient();
  const online = useOnline();
  const [text, setText] = useState('');
  const [marking, setMarking] = useState<NursingNoteView | null>(null);
  const requestId = useRef(crypto.randomUUID());
  const admitted = view.admission.status === 'admitted';
  const save = useMutation({
    mutationFn: () =>
      recordNursingNote(view.admission.id, { clientRequestId: requestId.current, text }),
    onSuccess: (next) => {
      queryClient.setQueryData(['inpatient', 'nursing', view.admission.id], next);
      requestId.current = crypto.randomUUID();
      setText('');
      toasts.show({ title: 'Note saved' });
    },
    onError: () => {
      requestId.current = crypto.randomUUID();
    },
  });
  const canSave = online && admitted && text.trim() !== '' && !save.isPending;
  // Ctrl Enter saves the vitals round on this page; a note is Ctrl Shift Enter.
  useShortcut(
    'mod+shift+enter',
    () => {
      if (canSave) save.mutate();
    },
    { allowInFields: true, description: 'Save nursing note', enabled: mayWrite },
  );
  return (
    <Section title="Nursing notes">
      {mayWrite && admitted ? (
        <form
          className={styles.form}
          aria-label="Write a nursing note"
          onSubmit={(event) => {
            event.preventDefault();
            if (canSave) save.mutate();
          }}
        >
          <TextArea
            label="Nursing note"
            value={text}
            onChange={setText}
            rows={3}
            description={`Up to ${String(NURSING_NOTE_MAX)} characters. Saved notes are not edited; enter one in error and write it again.`}
          />
          {save.error === null ? null : (
            <Notice tone="error">
              {errorText(save.error, 'The note was not saved. Try again.')}
            </Notice>
          )}
          <div className={styles.actions}>
            <Button
              type="submit"
              isDisabled={!canSave}
              isPending={save.isPending}
              pendingLabel="Saving…"
            >
              Save note
            </Button>
            <Kbd combo="mod+shift+enter" />
          </div>
        </form>
      ) : null}
      {view.nursingNotes.length === 0 ? (
        <p className={styles.meta}>No nursing notes in this stay yet.</p>
      ) : (
        <ul className={styles.list} aria-label="Nursing notes">
          {view.nursingNotes.map((note) => (
            <li key={note.id} className={styles.item} data-status={note.status}>
              <div className={styles.itemHead}>
                <span className={styles.meta}>
                  {`${when(note.notedAt)} · ${note.recordedBy.displayName} · ${note.wardName}`}
                </span>
                {note.status === 'entered_in_error' ? (
                  <Tag state="neutral">Entered in error</Tag>
                ) : mayWrite && note.recordedBy.id === session?.user.id ? (
                  <Button
                    variant="quiet"
                    dense
                    isDisabled={!online}
                    onPress={() => {
                      setMarking(note);
                    }}
                  >
                    Enter in error
                  </Button>
                ) : null}
              </div>
              <p className={styles.text}>{note.text}</p>
              {note.statusReason === null ? null : (
                <p className={styles.meta}>{`Reason: ${note.statusReason}`}</p>
              )}
            </li>
          ))}
        </ul>
      )}
      {marking === null ? null : (
        <InErrorDialog
          note={marking}
          admissionId={view.admission.id}
          onClose={() => {
            setMarking(null);
          }}
        />
      )}
    </Section>
  );
}

export function NursingPage() {
  const { admissionId } = route.useParams();
  const { data: session } = useSuspenseQuery(sessionQuery);
  const online = useOnline();
  const nursing = useQuery(nursingQuery(admissionId));

  if (session === null || !can(session, 'clinical.read') || !can(session, 'inpatient.read')) {
    return (
      <Notice tone="info">
        You don't have permission to see inpatient nursing records. Ask an administrator if you need
        it.
      </Notice>
    );
  }
  if (nursing.isPending) return <LoadingBlock label="Loading nursing" rows={8} />;
  if (nursing.isError) {
    return (
      <Notice
        tone="error"
        action={<Button onPress={() => void nursing.refetch()}>Try again</Button>}
      >
        {errorText(nursing.error, 'Nursing could not be loaded. Try again.')}
      </Notice>
    );
  }
  const view = nursing.data;
  const admitted = view.admission.status === 'admitted';
  const mayWrite = can(session, 'nursing.record');
  return (
    <div className={styles.page}>
      <PatientBannerLoader patientId={view.admission.patientId} />
      <PageHeader
        title="Nursing"
        description={placeWords(view)}
        actions={
          <>
            {can(session, 'consultation.record') ? (
              <Link to="/admissions/$admissionId/ward-round" params={{ admissionId }}>
                Ward round
              </Link>
            ) : null}
            <Link to="/admissions/$admissionId/medicines" params={{ admissionId }}>
              Medicines
            </Link>
            <Link to="/admissions/$admissionId" params={{ admissionId }}>
              Back to the stay
            </Link>
          </>
        }
      />
      {!online ? (
        <Notice tone="info">No connection to Wodi. Saving vitals and notes waits for it.</Notice>
      ) : null}
      {!admitted ? (
        <Notice tone="info">
          This stay has ended. Its records are shown; nothing new can be added.
        </Notice>
      ) : null}
      {mayWrite && admitted ? (
        <Section
          title="Record vitals"
          description="Values are checked against the clinical rules as you type."
        >
          <VitalsRoundForm view={view} />
        </Section>
      ) : null}
      <Section
        title="Vitals chart"
        description={
          view.earlyWarning.state === 'not_configured'
            ? 'Early warning score: not set up. The clinical lead chooses the scale and its thresholds.'
            : `Early warning score ${String(view.earlyWarning.score)} (${view.earlyWarning.riskBand})`
        }
      >
        <VitalsChart sets={view.vitalSets} />
        <VitalsTable sets={view.vitalSets} />
      </Section>
      <NursingNotes view={view} mayWrite={mayWrite} />
    </div>
  );
}
